import { useDispatch } from 'react-redux';
import type { Action } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import type { ReduxAction } from '../types';

import useOnMount from './useOnMount';

/**
 * Hook for dispatching an action on component mount using react-redux v7 hooks.
 * Use with useSelector from react-redux.
 *
 * @template T - An array of types defining the rest parameters after the actionCreator.
 *                The {@link https://www.typescriptlang.org/docs/handbook/utility-types.html#parameterstype Parameter}
 *                utility type can help with complex function parameter typings.
 * @param {(params: T) => ReduxAction} actionCreator - The function that creates the Redux action to be dispatched.
 * @param {...T} params - The parameters to be passed to the actionCreator.
 * @example
 * import React from 'react';
 * import useDispatchOnMount from '../hooks/useDispatchOnMount';
 * import ProjectStore from '../redux/projects';
 *
 * const MyComponent = () => {
 *  const { loading: isLoading } = useSelector(ProjectStore.selectors.getStatus);
 *  const branches = useSelector(ProjectStore.selectors.getBranchesForProject);
 *
 *  // <Parameters<typeof ProjectStore.actions.fetchBranches>> could also be used for the generic.
 *  useDispatchOnMount<[string]>(ProjectStore.actions.fetchBranches, 'npm+testLocator');
 *
 *   return (
 *     <div>
 *      { !isLoading && <div>{JSON.stringify(branches)}<div> }
 *     </div>
 *   );
 * }
 */
export default function useDispatchOnMount<T extends unknown[]>(
  actionCreator: (...args: T) => Action | ReduxAction | ThunkAction<void, unknown, never, Action>,
  ...params: T
) {
  const dispatch = useDispatch();

  useOnMount(() => {
    dispatch(actionCreator(...params));
  });
}
